import React, { useState, useEffect } from 'react';
import {
  X, FileSignature, Clock, CheckCircle, AlertCircle, Send,
  Eye, MessageSquare, ShieldCheck, Loader2, ExternalLink,
  Calendar, User, Mail, Phone, MapPin
} from 'lucide-react';
import { contracts as contractsApi } from '../../../services/api/client';

const ContractStatusDialog = ({ contract, onClose, onRefresh }) => {
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      if (!contract?.id) return;
      try {
        setLoading(true);
        const logs = await contractsApi.getAccessLogs(contract.id);
        setAccessLogs(logs || []);
      } catch (error) {
        console.error('Error fetching access logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [contract?.id]);

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Brouillon', color: 'gray', icon: FileSignature },
      pending_signature: { label: 'En attente de signature', color: 'yellow', icon: Clock },
      signed: { label: 'Signé', color: 'green', icon: CheckCircle },
      active: { label: 'Actif', color: 'blue', icon: ShieldCheck },
      completed: { label: 'Terminé', color: 'purple', icon: CheckCircle },
      expired: { label: 'Expiré', color: 'red', icon: AlertCircle },
      cancelled: { label: 'Annulé', color: 'gray', icon: X }
    };
    return configs[status] || configs.draft;
  };

  const getActionConfig = (action) => {
    const configs = {
      viewed: { label: 'Contrat consulté', icon: Eye, color: 'blue' },
      sms_requested: { label: 'Code SMS demandé', icon: MessageSquare, color: 'purple' },
      sms_verified: { label: 'Code SMS vérifié', icon: ShieldCheck, color: 'green' },
      signed: { label: 'Contrat signé', icon: CheckCircle, color: 'green' },
      downloaded: { label: 'PDF téléchargé', icon: FileSignature, color: 'blue' },
      sent: { label: 'Contrat envoyé', icon: Send, color: 'yellow' }
    };
    return configs[action] || { label: action, icon: Clock, color: 'gray' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusConfig = getStatusConfig(contract?.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${statusConfig.color}-500/20`}>
              <StatusIcon className={`w-6 h-6 text-${statusConfig.color}-400`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Statut du contrat</h2>
              <p className="text-sm text-gray-400">{contract?.contract_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Status Badge */}
          <div className="mb-6">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-${statusConfig.color}-500/20 border border-${statusConfig.color}-500/30`}>
              <StatusIcon className={`w-5 h-5 text-${statusConfig.color}-400`} />
              <span className={`font-medium text-${statusConfig.color}-400`}>{statusConfig.label}</span>
            </div>
          </div>

          {/* Contract Info */}
          <div className="bg-brand-black rounded-lg border border-gray-700 p-4 mb-6">
            <h3 className="text-white font-medium mb-3">{contract?.title}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <User className="w-4 h-4" />
                <span>{contract?.client_representative_name || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>{contract?.client_contact_email || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>
                  {contract?.start_date ? new Date(contract.start_date).toLocaleDateString('fr-FR') : '-'}
                  {' - '}
                  {contract?.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : '-'}
                </span>
              </div>
              {contract?.signed_at && (
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>Signé le {formatDate(contract.signed_at)}</span>
                </div>
              )}
            </div>

            {/* Signature URL */}
            {contract?.signature_url && contract?.status === 'pending_signature' && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-xs text-gray-500 mb-2">Lien de signature :</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}${contract.signature_url}`}
                    className="flex-1 bg-brand-black border border-gray-600 rounded px-3 py-2 text-sm text-gray-300"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}${contract.signature_url}`);
                    }}
                    className="px-3 py-2 bg-brand-green/20 text-brand-green rounded hover:bg-brand-green/30 transition-colors text-sm"
                  >
                    Copier
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-brand-green" />
              Historique des événements
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
              </div>
            ) : accessLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucun événement enregistré</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-700" />

                {/* Timeline items */}
                <div className="space-y-4">
                  {accessLogs.map((log, index) => {
                    const actionConfig = getActionConfig(log.action);
                    const ActionIcon = actionConfig.icon;

                    return (
                      <div key={log.id || index} className="relative flex items-start gap-4 pl-10">
                        {/* Timeline dot */}
                        <div className={`absolute left-2 w-5 h-5 rounded-full bg-${actionConfig.color}-500/20 border-2 border-${actionConfig.color}-500 flex items-center justify-center`}>
                          <ActionIcon className={`w-2.5 h-2.5 text-${actionConfig.color}-400`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 bg-brand-black rounded-lg border border-gray-700 p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium text-${actionConfig.color}-400`}>
                              {actionConfig.label}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(log.created_at)}
                            </span>
                          </div>
                          {log.ip_address && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <MapPin className="w-3 h-3" />
                              <span>IP : {log.ip_address}</span>
                            </div>
                          )}
                          {log.geolocation && (
                            <div className="text-xs text-gray-500 mt-1">
                              {log.geolocation.city && `${log.geolocation.city}, `}
                              {log.geolocation.country}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractStatusDialog;
